import type { Components, Theme } from "@mui/material/styles";
import type { DesignTokens } from "./types";

export function buildComponents(tokens: DesignTokens): Components<Theme> {
  const {
    base,
    error,
    warning,
    success,
    info,
    highlight,
    radius,
    focusRings,
    shadows: tokenShadows,
    text,
    bg,
    fg,
    border,
    buttonColors,
    sliderColors,
    avatarColors,
    breadcrumbColors,
  } = tokens;

  return {
    // ─── CssBaseline (global) ───────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFeatureSettings: '"ss01", "ss02", "cv11"',
        },
      },
    },

    // ─── Button ─────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          fontWeight: 600,
          boxShadow: tokenShadows.xs,
          height: 32,
          padding: "6px 12px",
          fontSize: 14,
          lineHeight: "20px",
          "&:focus-visible": {
            boxShadow: focusRings.brandShadowXs,
          },
        },
        containedPrimary: {
          backgroundColor: buttonColors.primary.bg,
          "&:hover": {
            backgroundColor: buttonColors.primary.bgHover,
          },
        },
        outlinedPrimary: {
          borderColor: buttonColors.secondaryColor.border,
          color: buttonColors.secondaryColor.fg,
          "&:hover": {
            backgroundColor: buttonColors.secondaryColor.bgHover,
            borderColor: buttonColors.secondaryColor.borderHover,
          },
        },
        containedSecondary: {
          backgroundColor: buttonColors.secondary.bg,
          color: buttonColors.secondary.fg,
          border: `1px solid ${buttonColors.secondary.border}`,
          "&:hover": {
            backgroundColor: buttonColors.secondary.bgHover,
            borderColor: buttonColors.secondary.borderHover,
          },
        },
        text: {
          boxShadow: "none",
        },
        textPrimary: {
          color: buttonColors.tertiaryColor.fg,
          "&:hover": {
            backgroundColor: buttonColors.tertiaryColor.bgHover,
          },
        },
      },
    },

    // ─── Button Group ───────────────────────────────────────────────
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
        },
        grouped: {
          "&:not(:last-of-type)": {
            borderRight: `1px solid ${buttonColors.primary.borderHover}`,
          },
        },
      },
    },

    // ─── IconButton ─────────────────────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          padding: 6,
          "& .MuiSvgIcon-root": {
            fontSize: 20,
          },
        },
      },
    },

    // ─── FAB ────────────────────────────────────────────────────────
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: tokenShadows.md,
          "&:hover": {
            boxShadow: tokenShadows.lg,
          },
          "&:focus-visible": {
            boxShadow: focusRings.brandShadowSm,
          },
        },
        primary: {
          backgroundColor: buttonColors.primary.bg,
          "&:hover": {
            backgroundColor: buttonColors.primary.bgHover,
          },
        },
      },
    },

    // ─── TextField / Input ──────────────────────────────────────────
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          "&:has(.Mui-disabled)": {
            opacity: 0.5,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            color: text.placeholder,
            WebkitTextFillColor: text.placeholder,
            fontWeight: 400,
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          boxShadow: tokenShadows.xs,
          "&&:hover:not(.Mui-focused):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
            borderColor: border.brand,
          },
          "&&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: border.brandSolid,
            borderWidth: 1,
            boxShadow: "none",
          },
          "&&.Mui-error:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
            borderColor: border.error,
          },
          "&&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: border.errorSolid,
            boxShadow: "none",
          },
          "& .MuiInputAdornment-root .MuiIconButton-root": {
            color: fg.quaternary,
            "&:hover": {
              backgroundColor: bg.primaryHover,
              color: fg.brandPrimary,
            },
          },
        },
        notchedOutline: {
          borderColor: border.primary,
        },
        input: {
          padding: "6px 12px",
          fontSize: 14,
          lineHeight: "20px",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: text.placeholder,
          "&.Mui-focused": {
            color: text.placeholder,
          },
          "&.Mui-error": {
            color: text.errorPrimary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 500,
          color: text.placeholder,
          "&.Mui-focused": {
            color: text.placeholder,
          },
          "&.Mui-error": {
            color: text.errorPrimary,
          },
        },
        outlined: {
          transform: "translate(12px, 6px) scale(1)",
          "&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: "20px",
          marginTop: 6,
          marginLeft: 0,
          "&.Mui-error": {
            color: text.errorPrimary,
          },
        },
      },
    },

    // ─── Menu ──────────────────────────────────────────────────────
    MuiMenu: {
      defaultProps: {
        autoFocus: false,
      },
      styleOverrides: {
        root: {
          "& .MuiBackdrop-root": {
            backgroundColor: "transparent",
          },
        },
      },
    },

    // ─── Select ─────────────────────────────────────────────────────
    MuiSelect: {
      styleOverrides: {
        select: {
          "&.MuiSelect-select": {
            minHeight: "auto",
            padding: "6px 32px 6px 12px",
            fontSize: 14,
            lineHeight: "20px",
          },
        },
        icon: {
          color: text.placeholder,
          right: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          padding: "6px 8px",
          gap: 4,
          "&:hover": {
            backgroundColor: bg.primaryHover,
          },
          "&.Mui-selected": {
            backgroundColor: info[50],
            color: info[700],
            "&:hover": {
              backgroundColor: info[100],
            },
          },
        },
      },
    },

    // ─── Autocomplete ───────────────────────────────────────────────
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          boxShadow: tokenShadows.lg,
          border: `1px solid ${border.secondary}`,
          marginTop: 4,
        },
        inputRoot: {
          // Override Autocomplete's default padding so the input height stays 32px
          padding: "0 12px",
          "&.MuiOutlinedInput-root .MuiAutocomplete-input": {
            padding: "6px 0",
            fontSize: 14,
            lineHeight: "20px",
          },
        },
        option: {
          fontSize: 14,
          lineHeight: "20px",
          padding: "6px 12px",
          "&.Mui-focused": {
            backgroundColor: bg.primaryHover,
          },
          '&[aria-selected="true"]': {
            backgroundColor: info[50],
            color: info[700],
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: info[100],
          },
        },
        listbox: {
          padding: 4,
        },
      },
    },

    // ─── Checkbox ───────────────────────────────────────────────────
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: border.primary,
          borderRadius: radius.xs,
          padding: 0,
          width: 20,
          height: 20,
          "&:hover": {
            backgroundColor: "transparent",
            color: fg.brandPrimary,
          },
          "&.Mui-checked": {
            color: fg.brandPrimary,
          },
          "&.Mui-checked:hover": {
            color: buttonColors.primary.bgHover,
          },
          "&.Mui-focusVisible": {
            outline: "none",
            boxShadow: focusRings.brand,
            borderRadius: radius.xs,
          },
          "&.Mui-disabled": {
            color: border.primary,
          },
        },
      },
    },

    // ─── Radio ──────────────────────────────────────────────────────
    MuiRadio: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: border.primary,
          padding: 0,
          "&:hover": {
            backgroundColor: "transparent",
            color: fg.brandPrimary,
          },
          "&.Mui-checked": {
            color: fg.brandPrimary,
          },
          "&.Mui-checked:hover": {
            color: buttonColors.primary.bgHover,
          },
          "&.Mui-focusVisible": {
            outline: "none",
            boxShadow: focusRings.brand,
            borderRadius: radius.full,
          },
          "&.Mui-disabled": {
            color: border.primary,
          },
        },
      },
    },

    // ─── Switch ─────────────────────────────────────────────────────
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          width: 44,
          height: 24,
          padding: 0,
        },
        switchBase: {
          padding: 2,
          "&.Mui-checked": {
            transform: "translateX(20px)",
            color: fg.white,
            "& + .MuiSwitch-track": {
              backgroundColor: fg.brandPrimary,
              opacity: 1,
            },
          },
          "&.Mui-checked:hover + .MuiSwitch-track": {
            backgroundColor: buttonColors.primary.bgHover,
          },
          "&.Mui-focusVisible .MuiSwitch-thumb": {
            boxShadow: focusRings.brand,
          },
          "&.Mui-disabled .MuiSwitch-thumb": {
            color: bg.disabled,
          },
          "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.5,
          },
        },
        thumb: {
          width: 20,
          height: 20,
          boxShadow: "0px 1px 2px 0px #1018280d",
        },
        track: {
          borderRadius: radius.full,
          backgroundColor: border.secondary,
          opacity: 1,
        },
      },
    },

    // ─── Toggle Button ─────────────────────────────────────────────
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: border.primary,
          color: text.secondary,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: "20px",
          height: 32,
          padding: "6px 12px",
          textTransform: "none",
          "&:hover": {
            backgroundColor: bg.primaryHover,
            borderColor: border.primary,
          },
          "&.Mui-selected": {
            backgroundColor: bg.brandPrimary,
            color: buttonColors.secondaryColor.fg,
            borderColor: border.brandSolid,
            "&:hover": {
              backgroundColor: bg.brandSecondary,
            },
          },
          "&:focus-visible": {
            boxShadow: focusRings.brandShadowXs,
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          overflow: "hidden",
        },
        grouped: {
          borderRadius: 0,
          "&:not(:first-of-type)": {
            marginLeft: -1,
          },
          "&:first-of-type": {
            borderTopLeftRadius: radius.md,
            borderBottomLeftRadius: radius.md,
          },
          "&:last-of-type": {
            borderTopRightRadius: radius.md,
            borderBottomRightRadius: radius.md,
          },
        },
      },
    },



    // ─── Drawer (used by Sidebar) ─────────────────────────────────
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: bg.primary,
          borderRight: `1px solid ${border.secondary}`,
        },
      },
    },

    // ─── Card ───────────────────────────────────────────────────────
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.xl,
          border: `1px solid ${border.secondary}`,
          backgroundColor: bg.primary,
          boxShadow: tokenShadows.xs,
          overflow: "hidden",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "24px",
          "&:last-child": {
            paddingBottom: "24px",
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: "24px 24px 0 24px",
        },
        title: {
          fontSize: 18,
          fontWeight: 600,
          lineHeight: "28px",
        },
        subheader: {
          fontSize: 14,
          lineHeight: "20px",
          marginTop: 4,
        },
        action: {
          marginTop: -4,
          marginRight: -4,
        },
      },
    },

    // ─── Paper ──────────────────────────────────────────────────────
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: radius.lg,
        },
        elevation1: {
          boxShadow: tokenShadows.xs,
          border: `1px solid ${border.secondary}`,
        },
        elevation2: {
          boxShadow: tokenShadows.sm,
          border: `1px solid ${border.secondary}`,
        },
        elevation3: {
          boxShadow: tokenShadows.md,
          border: `1px solid ${border.secondary}`,
        },
        elevation4: {
          boxShadow: tokenShadows.lg,
          border: `1px solid ${border.secondary}`,
        },
        elevation8: {
          boxShadow: tokenShadows.xl,
          border: `1px solid ${border.secondary}`,
        },
        elevation16: {
          boxShadow: tokenShadows["2xl"],
          border: `1px solid ${border.secondary}`,
        },
        elevation24: {
          boxShadow: tokenShadows["3xl"],
          border: `1px solid ${border.secondary}`,
        },
      },
    },

    // ─── Accordion ──────────────────────────────────────────────────
    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
        elevation: 0,
      },
      styleOverrides: {
        root: {
          border: `1px solid ${border.secondary}`,
          borderRadius: radius.lg,
          "&:not(:last-child)": {
            marginBottom: 8,
          },
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            margin: 0,
            marginBottom: 8,
            borderRadius: radius.lg,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: "0 24px",
          minHeight: 56,
          "&.Mui-expanded": {
            minHeight: 56,
            borderBottom: `1px solid ${border.secondary}`,
          },
        },
        content: {
          margin: "12px 0",
          "&.Mui-expanded": {
            margin: "12px 0",
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: "24px",
        },
      },
    },

    // ─── AppBar ─────────────────────────────────────────────────────
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: "transparent",
      },
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${border.secondary}`,
          backgroundColor: bg.primary,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
          "@media (min-width: 600px)": {
            minHeight: 64,
          },
        },
        dense: {
          minHeight: 48,
          "@media (min-width: 600px)": {
            minHeight: 48,
          },
        },
      },
    },

    // ─── Tabs ──────────────────────────────────────────────────────────
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        indicator: {
          backgroundColor: info[600],
          height: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 48,
          height: 48,
          padding: "12px 16px",
          fontSize: 14,
          fontWeight: 600,
          lineHeight: "20px",
          textTransform: "none",
          color: text.tertiary,
          borderRadius: `${radius.md}px ${radius.md}px 0 0`,
          "&:hover": {
            backgroundColor: bg.primaryHover,
          },
          "&.Mui-selected": {
            color: info[700],
            backgroundColor: info[50],
          },
        },
      },
    },

    // ─── Chip ───────────────────────────────────────────────────────
    MuiChip: {
      variants: [
        ...(highlight
          ? [
              {
                props: { color: "highlight" as const },
                style: {
                  "&.MuiChip-filled": {
                    backgroundColor: highlight[600],
                    color: base.white,
                    "& .MuiChip-deleteIcon": { color: highlight[200] },
                  },
                  "&.MuiChip-outlined": {
                    borderColor: highlight[300],
                    color: highlight[700],
                    "& .MuiChip-deleteIcon": { color: highlight[400] },
                  },
                },
              },
            ]
          : []),
      ],
      styleOverrides: {
        root: {
          borderRadius: radius.md,
        },
        label: {
          fontWeight: 500,
        },
        sizeSmall: {
          fontSize: 12,
          height: 20,
        },
        sizeMedium: {
          fontSize: 14,
          height: 24,
          "& .MuiChip-deleteIcon": { fontSize: 16 },
        },
        colorDefault: {
          "&.MuiChip-filled": {
            backgroundColor: tokens.bg.secondaryHover,
            color: tokens.fg.secondary,
            "& .MuiChip-deleteIcon": { color: tokens.fg.quinary },
          },
        },
        colorPrimary: {
          "&.MuiChip-filled": {
            backgroundColor: tokens.bg.brandPrimary,
            color: tokens.fg.brandPrimary,
            "& .MuiChip-deleteIcon": { color: tokens.fg.brandPrimary },
          },
        },
        colorSecondary: {
          "&.MuiChip-filled": {
            backgroundColor: highlight ? highlight[50] : tokens.bg.secondarySubtle,
            color: highlight ? highlight[700] : tokens.text.secondary,
            "& .MuiChip-deleteIcon": {
              color: highlight ? highlight[500] : tokens.fg.secondary,
            },
          },
        },
        colorError: {
          "&.MuiChip-filled": {
            backgroundColor: error[50],
            color: error[700],
            "& .MuiChip-deleteIcon": { color: error[400] },
          },
        },
        colorWarning: {
          "&.MuiChip-filled": {
            backgroundColor: warning[50],
            color: warning[700],
            "& .MuiChip-deleteIcon": { color: warning[400] },
          },
        },
        colorSuccess: {
          "&.MuiChip-filled": {
            backgroundColor: success[50],
            color: success[700],
            "& .MuiChip-deleteIcon": { color: success[400] },
          },
        },
        colorInfo: {
          "&.MuiChip-filled": {
            backgroundColor: info[50],
            color: info[700],
            "& .MuiChip-deleteIcon": { color: info[400] },
          },
        },
      },
    },

    // ─── Form Control / Label ───────────────────────────────────────
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: 0,
          gap: 8,
        },
        label: {
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: 500,
          color: text.secondary,
        },
      },
    },

    // ─── Table ───────────────────────────────────────────────────────
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: "separate",
          borderSpacing: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${border.secondary}`,
          padding: "16px 24px",
          fontSize: 14,
          lineHeight: "20px",
          color: fg.tertiary,
        },
        head: {
          backgroundColor: bg.secondary,
          color: fg.tertiary,
          fontWeight: 500,
          fontSize: 12,
          lineHeight: "18px",
        },
        body: {
          color: fg.tertiary,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: bg.secondary,
          "& .MuiTableRow-root:last-child .MuiTableCell-root": {
            borderBottom: `1px solid ${border.secondary}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: bg.secondary,
          "&:last-child td, &:last-child th": {
            border: 0,
          },
          "&:hover": {
            backgroundColor: bg.primaryHover,
          },
          "&.Mui-selected": {
            backgroundColor: info[50],
            "&:hover": {
              backgroundColor: info[100],
            },
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: radius.xl,
          border: `1px solid ${border.secondary}`,
          boxShadow: "none",
        },
      },
    },

    // ─── Badge ──────────────────────────────────────────────────────
    MuiBadge: {
      styleOverrides: {
        standard: {
          fontWeight: 500,
          fontSize: 12,
          lineHeight: "18px",
          minWidth: 20,
          height: 20,
          borderRadius: radius.full,
        },
        colorPrimary: {
          backgroundColor: bg.brandSolid,
        },
        colorError: {
          backgroundColor: bg.errorSolid,
        },
        colorSuccess: {
          backgroundColor: bg.successSolid,
        },
        colorWarning: {
          backgroundColor: bg.warningSolid,
        },
        dot: {
          minWidth: 8,
          height: 8,
          borderRadius: radius.full,
          ...(highlight && {
            "&.MuiBadge-colorDefault, &.MuiBadge-colorPrimary": {
              backgroundColor: highlight[500],
            },
          }),
        },
      },
    },

    // ─── Divider ────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: border.secondary,
        },
      },
    },

    // ─── List ───────────────────────────────────────────────────────
    MuiList: {
      styleOverrides: {
        root: {
          padding: 4,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          padding: "8px 12px",
          "&:hover": {
            backgroundColor: bg.primaryHover,
          },
          "&.Mui-selected": {
            backgroundColor: info[50],
            color: info[700],
            "&:hover": {
              backgroundColor: info[100],
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 36,
          color: fg.quaternary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: 500,
          color: text.secondary,
        },
        secondary: {
          fontSize: 14,
          lineHeight: "20px",
          color: text.quaternary,
        },
      },
    },

    // ─── Tooltip ────────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: bg.primarySolid,
          color: text.white,
          fontSize: 12,
          lineHeight: "18px",
          fontWeight: 600,
          padding: "8px 12px",
          borderRadius: radius.md,
          boxShadow: tokenShadows.lg,
        },
        arrow: {
          color: bg.primarySolid,
        },
      },
    },

    // ─── Alert ──────────────────────────────────────────────────────
    MuiAlert: {
      defaultProps: {
        variant: "standard",
      },
      variants: [
        ...(highlight
          ? [
              {
                props: { variant: "featured" as const },
                style: {
                  borderRadius: radius.xl,
                  fontSize: 14,
                  lineHeight: "20px",
                  padding: "12px 16px",
                  alignItems: "center",
                  backgroundColor: highlight[50],
                  color: highlight[700],
                  border: `1px solid ${highlight[200]}`,
                  "& .MuiAlert-icon": {
                    color: highlight[600],
                    padding: 0,
                    marginRight: 12,
                  },
                  "& .MuiAlertTitle-root": {
                    color: highlight[800],
                  },
                },
              },
            ]
          : []),
      ],
      styleOverrides: {
        root: {
          borderRadius: radius.xl,
          fontSize: 14,
          lineHeight: "20px",
          padding: "12px 16px",
          alignItems: "center",
        },
        standardError: {
          backgroundColor: bg.errorPrimary,
          color: error[700],
          border: `1px solid ${border.error}`,
          "& .MuiAlert-icon": {
            color: fg.errorPrimary,
          },
        },
        standardWarning: {
          backgroundColor: bg.warningPrimary,
          color: warning[700],
          border: `1px solid ${warning[300]}`,
          "& .MuiAlert-icon": {
            color: fg.warningPrimary,
          },
        },
        standardSuccess: {
          backgroundColor: bg.successPrimary,
          color: success[700],
          border: `1px solid ${success[300]}`,
          "& .MuiAlert-icon": {
            color: fg.successPrimary,
          },
        },
        standardInfo: {
          backgroundColor: info[50],
          color: info[700],
          border: `1px solid ${info[300]}`,
          "& .MuiAlert-icon": {
            color: info[600],
          },
        },
        icon: {
          padding: 0,
          marginRight: 12,
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: 14,
          lineHeight: "20px",
          marginBottom: 4,
        },
      },
    },

    // ─── Backdrop ───────────────────────────────────────────────────
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(16, 24, 40, 0.7)",
        },
      },
    },

    // ─── Dialog ─────────────────────────────────────────────────────
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.xl,
          boxShadow: tokenShadows.xl,
          padding: 0,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 18,
          lineHeight: "28px",
          fontWeight: 600,
          color: text.primary,
          padding: "24px 24px 0",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: "20px 24px",
          fontSize: 14,
          lineHeight: "20px",
          color: text.tertiary,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "0 24px 24px",
          gap: 12,
        },
      },
    },

    // ─── Progress ───────────────────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: radius.full,
          height: 8,
          backgroundColor: border.secondary,
        },
        barColorPrimary: {
          backgroundColor: fg.brandPrimary,
          borderRadius: radius.full,
        },
        barColorSecondary: {
          backgroundColor: bg.secondarySolid,
          borderRadius: radius.full,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        colorPrimary: {
          color: fg.brandPrimary,
        },
        colorSecondary: {
          color: bg.secondarySolid,
        },
      },
    },

    // ─── Skeleton ───────────────────────────────────────────────────
    MuiSkeleton: {
      defaultProps: {
        animation: "wave",
      },
      styleOverrides: {
        root: {
          backgroundColor: bg.tertiary,
        },
        rounded: {
          borderRadius: radius.md,
        },
        circular: {
          borderRadius: radius.full,
        },
      },
    },

    // ─── Snackbar ───────────────────────────────────────────────────
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: bg.primarySolid,
          color: text.white,
          borderRadius: radius.xl,
          boxShadow: tokenShadows.lg,
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: 500,
          padding: "6px 16px",
        },
      },
    },

    // ─── Typography ─────────────────────────────────────────────────
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: "h1",
          h2: "h2",
          h3: "h3",
          h4: "h4",
          h5: "h5",
          h6: "h6",
          subtitle1: "p",
          subtitle2: "p",
          body1: "p",
          body2: "p",
          caption: "span",
          overline: "span",
        },
      },
    },

    // ─── Slider ─────────────────────────────────────────────────────
    MuiSlider: {
      styleOverrides: {
        root: {
          color: fg.brandPrimary,
          height: 8,
          "&.Mui-disabled": {
            color: border.secondary,
          },
        },
        thumb: {
          width: 20,
          height: 20,
          backgroundColor: sliderColors.handleBg,
          border: `2px solid ${sliderColors.handleBorder}`,
          boxShadow: tokenShadows.xs,
          "&:hover, &.Mui-focusVisible": {
            boxShadow: focusRings.brandShadowXs,
          },
          "&.Mui-active": {
            boxShadow: focusRings.brandShadowSm,
          },
        },
        track: {
          borderRadius: radius.full,
          border: "none",
        },
        rail: {
          backgroundColor: border.secondary,
          borderRadius: radius.full,
          opacity: 1,
        },
        valueLabel: {
          backgroundColor: bg.primarySolid,
          borderRadius: radius.md,
          fontSize: 12,
          fontWeight: 600,
          padding: "4px 8px",
        },
        mark: {
          backgroundColor: border.secondary,
          width: 4,
          height: 4,
          borderRadius: radius.full,
        },
        markActive: {
          backgroundColor: fg.brandPrimary,
        },
        markLabel: {
          fontSize: 12,
          color: text.tertiary,
        },
      },
    },

    // ─── Rating ─────────────────────────────────────────────────────
    MuiRating: {
      styleOverrides: {
        root: {
          color: fg.brandPrimary,
          gap: 4,
        },
        iconEmpty: {
          color: border.secondary,
        },
        iconFilled: {
          color: fg.brandPrimary,
        },
        iconHover: {
          color: buttonColors.primary.bgHover,
        },
      },
    },

    // ─── Pagination ─────────────────────────────────────────────────
    MuiPagination: {
      defaultProps: {
        shape: "rounded",
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          fontSize: 14,
          fontWeight: 500,
          color: text.secondary,
          "&:hover": {
            backgroundColor: bg.primaryHover,
          },
          "&.Mui-selected": {
            backgroundColor: fg.brandPrimary,
            color: base.white,
            "&:hover": {
              backgroundColor: buttonColors.primary.bgHover,
            },
          },
          "&.Mui-focusVisible": {
            boxShadow: focusRings.brandShadowXs,
          },
        },
        outlined: {
          border: `1px solid ${border.primary}`,
          "&.Mui-selected": {
            backgroundColor: fg.brandPrimary,
            color: base.white,
            borderColor: fg.brandPrimary,
          },
        },
      },
    },

    // ─── Avatar ─────────────────────────────────────────────────────
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: avatarColors.bg,
          fontSize: 14,
          fontWeight: 600,
          border: `1.5px solid ${avatarColors.contrastBorder}`,
        },
        rounded: {
          borderRadius: radius.md,
        },
      },
    },
    MuiAvatarGroup: {
      styleOverrides: {
        avatar: {
          border: `2px solid ${avatarColors.profilePhotoBorder}`,
          width: 32,
          height: 32,
          fontSize: 13,
          fontWeight: 600,
        },
      },
    },

    // ─── BottomNavigation ───────────────────────────────────────────
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: bg.primary,
          borderTop: `1px solid ${border.secondary}`,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: fg.quaternary,
          "&.Mui-selected": {
            color: fg.brandPrimary,
          },
        },
        label: {
          fontSize: 12,
          fontWeight: 500,
          "&.Mui-selected": {
            fontSize: 12,
            fontWeight: 600,
          },
        },
      },
    },

    // ─── SpeedDial ──────────────────────────────────────────────────
    MuiSpeedDial: {
      styleOverrides: {
        fab: {
          backgroundColor: fg.brandPrimary,
          color: base.white,
          boxShadow: tokenShadows.md,
          "&:hover": {
            backgroundColor: buttonColors.primary.bgHover,
          },
        },
      },
    },
    MuiSpeedDialAction: {
      styleOverrides: {
        fab: {
          backgroundColor: bg.primary,
          color: fg.secondary,
          boxShadow: tokenShadows.sm,
          border: `1px solid ${border.secondary}`,
          "&:hover": {
            backgroundColor: bg.primaryHover,
          },
        },
        staticTooltipLabel: {
          backgroundColor: bg.primarySolid,
          color: text.white,
          fontSize: 12,
          fontWeight: 600,
          borderRadius: radius.md,
          whiteSpace: "nowrap",
        },
      },
    },

    // ─── Stepper ────────────────────────────────────────────────────
    MuiStepper: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: 14,
          fontWeight: 500,
          color: text.tertiary,
          "&.Mui-active": {
            color: text.primary,
            fontWeight: 600,
          },
          "&.Mui-completed": {
            color: text.primary,
            fontWeight: 500,
          },
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: border.secondary,
          "&.Mui-active": {
            color: fg.brandPrimary,
          },
          "&.Mui-completed": {
            color: fg.brandPrimary,
          },
        },
        text: {
          fontSize: 12,
          fontWeight: 600,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: border.secondary,
        },
        lineHorizontal: {
          borderTopWidth: 2,
        },
        lineVertical: {
          borderLeftWidth: 2,
        },
      },
    },

    // ─── MobileStepper ──────────────────────────────────────────────
    MuiMobileStepper: {
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          padding: "8px 0",
        },
        dot: {
          backgroundColor: border.secondary,
          width: 8,
          height: 8,
        },
        dotActive: {
          backgroundColor: fg.brandPrimary,
        },
        progress: {
          width: "100%",
          backgroundColor: border.secondary,
          borderRadius: radius.full,
          "& .MuiLinearProgress-bar": {
            backgroundColor: fg.brandPrimary,
            borderRadius: radius.full,
          },
        },
      },
    },

    // ─── Breadcrumbs ────────────────────────────────────────────────
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: 14,
          lineHeight: "20px",
        },
        separator: {
          color: breadcrumbColors.iconFg,
          marginLeft: 4,
          marginRight: 4,
        },
        li: {
          "& a": {
            color: breadcrumbColors.fg,
            textDecoration: "none",
            fontWeight: 500,
            "&:hover": {
              color: breadcrumbColors.fgHover,
            },
          },
        },
      },
    },

    // ─── Link ───────────────────────────────────────────────────────
    MuiLink: {
      styleOverrides: {
        root: {
          color: info[600],
          fontWeight: 500,
          textDecorationColor: info[600],
          "&:hover": {
            color: info[700],
            textDecorationColor: info[700],
          },
        },
      },
    },

    // ─── Popover ────────────────────────────────────────────────────
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          boxShadow: tokenShadows.lg,
          border: `1px solid ${border.secondary}`,
        },
      },
    },

    // ─── Snackbar ───────────────────────────────────────────────────
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
      },
    },

    // ─── CardActionArea ─────────────────────────────────────────────
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          "&:hover .MuiCardActionArea-focusHighlight": {
            opacity: 0.04,
          },
          "&.Mui-focusVisible .MuiCardActionArea-focusHighlight": {
            opacity: 0.08,
          },
        },
      },
    },

    // ─── FilledInput ────────────────────────────────────────────────
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: `${radius.md}px ${radius.md}px 0 0`,
          backgroundColor: bg.secondary,
          "&:hover": {
            backgroundColor: bg.secondaryHover,
          },
          "&.Mui-focused": {
            backgroundColor: bg.secondary,
          },
          "&:after": {
            borderBottomColor: fg.brandPrimary,
          },
        },
        input: {
          padding: "6px 12px",
          fontSize: 14,
          lineHeight: "20px",
        },
      },
    },

    // ─── TablePagination ────────────────────────────────────────────
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: 14,
          color: text.tertiary,
        },
        selectLabel: {
          fontSize: 14,
          color: text.tertiary,
        },
        displayedRows: {
          fontSize: 14,
          color: text.tertiary,
        },
        select: {
          fontSize: 14,
        },
        actions: {
          "& .MuiIconButton-root": {
            color: fg.quaternary,
            "&:hover": {
              backgroundColor: bg.primaryHover,
              color: fg.brandPrimary,
            },
          },
        },
      },
    },

    // ─── TableSortLabel ─────────────────────────────────────────────
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          "&:hover": {
            color: fg.secondary,
          },
          "&.Mui-active": {
            color: fg.secondary,
            "& .MuiTableSortLabel-icon": {
              color: fg.brandPrimary,
            },
          },
        },
      },
    },

    // ─── TabScrollButton ────────────────────────────────────────────
    MuiTabScrollButton: {
      styleOverrides: {
        root: {
          color: fg.quaternary,
          "&:hover": {
            color: fg.brandPrimary,
          },
        },
      },
    },

    // ─── DataGrid (@mui/x-data-grid) ──────────────────────────────
    // Not part of MUI core's Components<Theme> type, so we merge via assertion.
    ...({
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${border.secondary}`,
            borderRadius: radius.xl,
            fontFamily: "inherit",
            "& .MuiDataGrid-withBorderColor": {
              borderColor: border.secondary,
            },
          },
          columnHeaders: {
            backgroundColor: bg.secondary,
          },
          columnHeader: {
            fontSize: 12,
            lineHeight: "18px",
            fontWeight: 500,
            color: fg.tertiary,
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
          columnHeaderTitle: {
            fontWeight: 500,
          },
          cell: {
            display: "flex",
            alignItems: "center",
            fontSize: 14,
            lineHeight: "20px",
            color: fg.tertiary,
            "&:focus, &:focus-within": {
              outline: "none",
            },
          },
          row: {
            backgroundColor: bg.secondary,
            "&:hover": {
              backgroundColor: bg.primaryHover,
            },
            "&.Mui-selected": {
              backgroundColor: bg.brandPrimary,
              "&:hover": {
                backgroundColor: bg.brandPrimary,
              },
            },
          },
          footerContainer: {
            borderTop: `1px solid ${border.secondary}`,
          },
          columnSeparator: {
            color: border.secondary,
          },
        },
      },
    } as Record<string, unknown>),

    // ─── DatePicker (@mui/x-date-pickers) ────────────────────────────
    // MUI X uses its own sub-components that are separate from MuiOutlinedInput,
    // so height/padding and brand colours must be set here independently.
    ...(({
      // Input field — mirrors MuiOutlinedInput sizing
      MuiPickersTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-root": {
              height: 32,
              borderRadius: radius.md,
              boxShadow: tokenShadows.xs,
              fontSize: 14,
              lineHeight: "20px",
            },
            "& .MuiInputBase-input": {
              padding: "6px 12px",
              fontSize: 14,
              lineHeight: "20px",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: border.primary,
            },
            "&:hover:not(:has(.Mui-disabled)) .MuiOutlinedInput-notchedOutline": {
              borderColor: border.brand,
            },
            "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: border.brandSolid,
              borderWidth: 1,
              boxShadow: "none",
            },
            "& .MuiInputAdornment-root .MuiIconButton-root": {
              color: fg.quaternary,
              "&:hover": {
                backgroundColor: bg.primaryHover,
                color: fg.brandPrimary,
              },
            },
          },
        },
      },

      // Calendar popup container
      MuiPickersPopper: {
        styleOverrides: {
          paper: {
            borderRadius: radius.xl,
            boxShadow: tokenShadows.lg,
            border: `1px solid ${border.secondary}`,
            marginTop: 4,
          },
        },
      },

      // Calendar layout
      MuiDateCalendar: {
        styleOverrides: {
          root: {
            width: 280,
            height: "auto",
          },
        },
      },

      // Month/year header navigation
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            paddingLeft: 16,
            paddingRight: 8,
            marginTop: 8,
          },
          label: {
            fontSize: 14,
            fontWeight: 600,
            color: text.primary,
          },
          switchViewButton: {
            color: fg.quaternary,
            "&:hover": { backgroundColor: bg.primaryHover, color: fg.brandPrimary },
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: {
            color: fg.quaternary,
            "&:hover": { backgroundColor: bg.primaryHover, color: fg.brandPrimary },
          },
        },
      },

      // Day/week header labels
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: {
            fontSize: 12,
            fontWeight: 500,
            color: text.tertiary,
            width: 32,
            height: 32,
          },
          weekContainer: {
            margin: "2px 0",
          },
        },
      },

      // Individual day cells
      MuiPickersDay: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          root: {
            fontSize: 13,
            fontWeight: 400,
            width: 32,
            height: 32,
            borderRadius: radius.md,
            color: text.primary,
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: bg.primaryHover,
            },
            "&.Mui-selected": {
              backgroundColor: info[600],
              color: base.white,
              fontWeight: 600,
              "&:hover": {
                backgroundColor: info[700],
              },
              "&:focus": {
                backgroundColor: info[600],
              },
            },
            "&.MuiPickersDay-today": {
              border: `1px solid ${info[600]}`,
              color: info[700],
              backgroundColor: "transparent",
              fontWeight: 600,
              "&.Mui-selected": {
                backgroundColor: info[600],
                color: base.white,
                border: "none",
              },
            },
            "&.Mui-focusVisible": {
              boxShadow: focusRings.brand,
            },
            "&.Mui-disabled": {
              color: text.disabled,
            },
          },
        },
      },

      // Year picker cells
      MuiPickersYear: {
        styleOverrides: {
          yearButton: {
            fontSize: 13,
            borderRadius: radius.md,
            "&:hover": { backgroundColor: bg.primaryHover },
            "&.Mui-selected": {
              backgroundColor: fg.brandPrimary,
              color: base.white,
              "&:hover": { backgroundColor: buttonColors.primary.bgHover },
            },
          },
        },
      },

      // Month picker cells
      MuiPickersMonth: {
        styleOverrides: {
          monthButton: {
            fontSize: 13,
            borderRadius: radius.md,
            "&:hover": { backgroundColor: bg.primaryHover },
            "&.Mui-selected": {
              backgroundColor: fg.brandPrimary,
              color: base.white,
              "&:hover": { backgroundColor: buttonColors.primary.bgHover },
            },
          },
        },
      },
    }) as Record<string, unknown>),
  };
}

